import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { highlightMarkdown } from '../common/MarkdownHighlighter';
import { formatCost, formatLatency } from '../../utils/formatters';
import { getModelDisplayName } from '../../constants/models';

const ComparisonResults = ({ results }) => {
  const [activeResultTab, setActiveResultTab] = useState('input');
  const [activePromptType, setActivePromptType] = useState('system');

  if (!results) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-text-secondary">No results to display</p>
      </div>
    );
  }

  const model1Name = results.model_1?.model || results.llm_config_1?.model || 'Model 1';
  const model2Name = results.model_2?.model || results.llm_config_2?.model || 'Model 2';
  const output1 = results.model_1?.output || results.output_1 || '';
  const output2 = results.model_2?.output || results.output_2 || '';
  const latency1 = results.model_1?.latency_ms || results.latency_ms_1 || 0;
  const latency2 = results.model_2?.latency_ms || results.latency_ms_2 || 0;
  const cost1 = results.model_1?.estimated_cost || results.estimated_cost_1 || 0;
  const cost2 = results.model_2?.estimated_cost || results.estimated_cost_2 || 0;
  const tokens1 = results.model_1?.token_usage || results.token_usage_1;
  const tokens2 = results.model_2?.token_usage || results.token_usage_2;
  const systemPrompt = results.system_prompt || '';
  const userPrompt = results.user_prompt || '';

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <>
      {/* Tabs */}
      <div className="pd-tabs">
        <button
          className={`pd-tab ${activeResultTab === 'input' ? 'active' : ''}`}
          onClick={() => setActiveResultTab('input')}
        >
          Input Values
        </button>
        <button
          className={`pd-tab ${activeResultTab === 'outputs' ? 'active' : ''}`}
          onClick={() => setActiveResultTab('outputs')}
        >
          Outputs
        </button>
        <button
          className={`pd-tab ${activeResultTab === 'metrics' ? 'active' : ''}`}
          onClick={() => setActiveResultTab('metrics')}
        >
          Metrics
        </button>
        <button
          className={`pd-tab ${activeResultTab === 'prompts' ? 'active' : ''}`}
          onClick={() => setActiveResultTab('prompts')}
        >
          Prompts Used
        </button>
      </div>

      {/* Content */}
      <div className="pd-content">
        {activeResultTab === 'input' && (
          <div className="pd-input-section">
            <div className="pd-grid">
              <div className="pd-card">
                <h3 className="pd-section-title">Prompt</h3>
                <div className="pd-card-value">{results.project_name || 'N/A'}</div>
              </div>
              <div className="pd-card">
                <h3 className="pd-section-title">Model 1</h3>
                <div className="pd-card-value">{getModelDisplayName(model1Name)}</div>
              </div>
              <div className="pd-card">
                <h3 className="pd-section-title">Model 2</h3>
                <div className="pd-card-value">{getModelDisplayName(model2Name)}</div>
              </div>
              <div className="pd-card">
                <h3 className="pd-section-title">Created</h3>
                <div className="pd-card-value">{results.created_at ? new Date(results.created_at).toLocaleDateString() : 'N/A'}</div>
              </div>
            </div>
            
            {results.input_values && Object.keys(results.input_values).length > 0 && (
              <div className="pd-card">
                <h3 className="pd-section-title">Input Variables</h3>
                <div className="pd-variables">
                  {Object.entries(results.input_values).map(([key, value]) => (
                    <div key={key} className="pd-variable-row">
                      <span className="pd-variable-name">{key}</span>
                      <span className="pd-variable-desc">
                        {Array.isArray(value) ? value.join(', ') : value}
                      </span>
                      <span className="pd-badge">{Array.isArray(value) ? 'Array' : 'String'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeResultTab === 'outputs' && (
          <div className="prompts-view grid grid-cols-[1fr_auto_1fr] gap-0 h-full">
            {/* Model 1 Output */}
            <div className="output-panel">
              <div className="output-tabs-container">
                <div className="output-tabs">
                  <span className="output-tab active">{getModelDisplayName(model1Name)}</span>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-text-tertiary">{formatLatency(latency1)} · {formatCost(cost1)}</span>
                  <button onClick={() => handleCopy(output1)} className="btn-copy">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    Copy
                  </button>
                </div>
              </div>
              <pre className="panel-content md-highlighted">
                {highlightMarkdown(output1)}
              </pre>
            </div>

            {/* Divider */}
            <div className="w-px bg-white/10 mx-4"></div>

            {/* Model 2 Output */}
            <div className="output-panel">
              <div className="output-tabs-container">
                <div className="output-tabs">
                  <span className="output-tab active">{getModelDisplayName(model2Name)}</span>
                </div>
                <div className="flex gap-2 items-center">
                  <span className="text-xs text-text-tertiary">{formatLatency(latency2)} · {formatCost(cost2)}</span>
                  <button onClick={() => handleCopy(output2)} className="btn-copy">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    Copy
                  </button>
                </div>
              </div>
              <pre className="panel-content md-highlighted">
                {highlightMarkdown(output2)}
              </pre>
            </div>
          </div>
        )}

        {activeResultTab === 'metrics' && (
          <div className="pd-input-section">
            <div className="space-y-6">
              {/* Winner Summary */}
              <div className="bg-bg-secondary border border-border-primary rounded-lg p-6">
                <h3 className="text-sm font-semibold text-text-primary mb-4 pb-2 border-b border-border-primary">
                  Comparison Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Fastest */}
                  <div className="bg-bg-tertiary rounded-lg p-4 border border-border-primary">
                    <div className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">Fastest</div>
                    <div className="text-base font-bold text-text-primary mb-1 truncate">
                      {latency1 <= latency2 ? getModelDisplayName(model1Name) : getModelDisplayName(model2Name)}
                    </div>
                    <div className="text-sm text-text-secondary font-mono">
                      {formatLatency(Math.min(latency1, latency2))}
                    </div>
                    {latency1 !== latency2 && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        {Math.round(((Math.max(latency1, latency2) - Math.min(latency1, latency2)) / Math.max(latency1, latency2)) * 100)}% faster
                      </div>
                    )}
                  </div>

                  {/* Cheapest */}
                  <div className="bg-bg-tertiary rounded-lg p-4 border border-border-primary">
                    <div className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">Cheapest</div>
                    <div className="text-base font-bold text-text-primary mb-1 truncate">
                      {cost1 <= cost2 ? getModelDisplayName(model1Name) : getModelDisplayName(model2Name)}
                    </div>
                    <div className="text-sm text-text-secondary font-mono">
                      {formatCost(Math.min(cost1, cost2))}
                    </div>
                    {cost1 !== cost2 && (
                      <div className="text-xs text-green-600 font-medium mt-1">
                        {Math.round(((Math.max(cost1, cost2) - Math.min(cost1, cost2)) / Math.max(cost1, cost2)) * 100)}% cheaper
                      </div>
                    )}
                  </div>

                  {/* Best Value */}
                  <div className="bg-bg-tertiary rounded-lg p-4 border border-border-primary">
                    <div className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">Best Value</div>
                    <div className="text-xs text-text-secondary mb-2">Cost per second</div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-text-tertiary truncate">{getModelDisplayName(model1Name)}</span>
                        <span className="text-xs font-mono text-text-primary font-medium">
                          {formatCost((cost1 / (latency1 / 1000)) || 0)}/s
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-text-tertiary truncate">{getModelDisplayName(model2Name)}</span>
                        <span className="text-xs font-mono text-text-primary font-medium">
                          {formatCost((cost2 / (latency2 / 1000)) || 0)}/s
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Metrics Comparison */}
              <div className="bg-bg-secondary border border-border-primary rounded-lg p-6">
                <h3 className="text-sm font-semibold text-text-primary mb-4 pb-2 border-b border-border-primary">
                  Performance Comparison
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Metric */}
                  <div>
                    <div className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-3">Metric</div>
                    <div className="space-y-2.5">
                      <div className="text-sm font-medium text-text-primary py-2">Status</div>
                      <div className="text-sm font-medium text-text-primary py-2">Latency</div>
                      <div className="text-sm font-medium text-text-primary py-2">Total Cost</div>
                      <div className="text-sm font-medium text-text-primary py-2">Input Cost</div>
                      <div className="text-sm font-medium text-text-primary py-2">Output Cost</div>
                    </div>
                  </div>
                  
                  {/* Model 1 */}
                  <div>
                    <div className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-3 truncate">
                      {getModelDisplayName(model1Name)}
                    </div>
                    <div className="space-y-2.5">
                      <div className="py-2">
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                          (results.status_1 || results.model_1?.status) === 'success' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {results.status_1 || results.model_1?.status || 'unknown'}
                        </span>
                      </div>
                      <div className="text-sm text-text-primary font-mono py-2">{formatLatency(latency1)}</div>
                      <div className="text-sm text-text-primary font-mono py-2 font-semibold">{formatCost(cost1)}</div>
                      <div className="text-sm text-text-secondary font-mono py-2">{formatCost(results.input_cost_1 || 0)}</div>
                      <div className="text-sm text-text-secondary font-mono py-2">{formatCost(results.output_cost_1 || 0)}</div>
                    </div>
                  </div>
                  
                  {/* Model 2 */}
                  <div>
                    <div className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-3 truncate">
                      {getModelDisplayName(model2Name)}
                    </div>
                    <div className="space-y-2.5">
                      <div className="py-2">
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
                          (results.status_2 || results.model_2?.status) === 'success' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {results.status_2 || results.model_2?.status || 'unknown'}
                        </span>
                      </div>
                      <div className="text-sm text-text-primary font-mono py-2">{formatLatency(latency2)}</div>
                      <div className="text-sm text-text-primary font-mono py-2 font-semibold">{formatCost(cost2)}</div>
                      <div className="text-sm text-text-secondary font-mono py-2">{formatCost(results.input_cost_2 || 0)}</div>
                      <div className="text-sm text-text-secondary font-mono py-2">{formatCost(results.output_cost_2 || 0)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Token Usage Comparison */}
              {tokens1 && tokens2 && (
                <div className="bg-bg-secondary border border-border-primary rounded-lg p-6">
                  <h3 className="text-sm font-semibold text-text-primary mb-4 pb-2 border-b border-border-primary">
                    Token Usage Comparison
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Metric */}
                    <div>
                      <div className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-3">Token Type</div>
                      <div className="space-y-2.5">
                        <div className="text-sm font-medium text-text-primary py-2">Input Tokens</div>
                        <div className="text-sm font-medium text-text-primary py-2">Output Tokens</div>
                        <div className="text-sm font-medium text-text-primary py-2">Total Tokens</div>
                      </div>
                    </div>
                    
                    {/* Model 1 */}
                    <div>
                      <div className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-3 truncate">
                        {getModelDisplayName(model1Name)}
                      </div>
                      <div className="space-y-2.5">
                        <div className="text-sm text-text-primary font-mono py-2">{tokens1.prompt_tokens?.toLocaleString() || 0}</div>
                        <div className="text-sm text-text-primary font-mono py-2">{tokens1.completion_tokens?.toLocaleString() || 0}</div>
                        <div className="text-sm text-text-primary font-mono py-2 font-semibold">{tokens1.total_tokens?.toLocaleString() || 0}</div>
                      </div>
                    </div>
                    
                    {/* Model 2 */}
                    <div>
                      <div className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-3 truncate">
                        {getModelDisplayName(model2Name)}
                      </div>
                      <div className="space-y-2.5">
                        <div className="text-sm text-text-primary font-mono py-2">{tokens2.prompt_tokens?.toLocaleString() || 0}</div>
                        <div className="text-sm text-text-primary font-mono py-2">{tokens2.completion_tokens?.toLocaleString() || 0}</div>
                        <div className="text-sm text-text-primary font-mono py-2 font-semibold">{tokens2.total_tokens?.toLocaleString() || 0}</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Cost Breakdown Visualization */}
              <div className="bg-bg-secondary border border-border-primary rounded-lg p-6">
                <h3 className="text-sm font-semibold text-text-primary mb-4 pb-2 border-b border-border-primary">
                  Cost Breakdown
                </h3>
                <div className="space-y-4">
                  {/* Model 1 */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-text-primary">{getModelDisplayName(model1Name)}</span>
                      <span className="text-sm font-mono font-semibold text-text-primary">{formatCost(cost1)}</span>
                    </div>
                    <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-bg-tertiary">
                      <div 
                        className="bg-primary-500" 
                        style={{ width: `${cost1 ? (results.input_cost_1 / cost1) * 100 : 50}%` }}
                        title={`Input: ${formatCost(results.input_cost_1 || 0)}`}
                      ></div>
                      <div 
                        className="bg-primary-700" 
                        style={{ width: `${cost1 ? (results.output_cost_1 / cost1) * 100 : 50}%` }}
                        title={`Output: ${formatCost(results.output_cost_1 || 0)}`}
                      ></div>
                    </div>
                    <div className="flex justify-between mt-1 text-xs">
                      <span className="text-text-tertiary">Input: {formatCost(results.input_cost_1 || 0)}</span>
                      <span className="text-text-tertiary">Output: {formatCost(results.output_cost_1 || 0)}</span>
                    </div>
                  </div>
                  
                  {/* Model 2 */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-text-primary">{getModelDisplayName(model2Name)}</span>
                      <span className="text-sm font-mono font-semibold text-text-primary">{formatCost(cost2)}</span>
                    </div>
                    <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-bg-tertiary">
                      <div 
                        className="bg-green-500" 
                        style={{ width: `${cost2 ? (results.input_cost_2 / cost2) * 100 : 50}%` }}
                        title={`Input: ${formatCost(results.input_cost_2 || 0)}`}
                      ></div>
                      <div 
                        className="bg-green-700" 
                        style={{ width: `${cost2 ? (results.output_cost_2 / cost2) * 100 : 50}%` }}
                        title={`Output: ${formatCost(results.output_cost_2 || 0)}`}
                      ></div>
                    </div>
                    <div className="flex justify-between mt-1 text-xs">
                      <span className="text-text-tertiary">Input: {formatCost(results.input_cost_2 || 0)}</span>
                      <span className="text-text-tertiary">Output: {formatCost(results.output_cost_2 || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeResultTab === 'prompts' && (
          <div className="prompts-view">
            <div className="output-tabs-container">
              <div className="output-tabs">
                {systemPrompt && (
                  <button
                    className={`output-tab ${activePromptType === 'system' ? 'active' : ''}`}
                    onClick={() => setActivePromptType('system')}
                  >
                    System Prompt
                  </button>
                )}
                {userPrompt && (
                  <button
                    className={`output-tab ${activePromptType === 'user' ? 'active' : ''}`}
                    onClick={() => setActivePromptType('user')}
                  >
                    User Prompt
                  </button>
                )}
              </div>
              <button
                onClick={() => {
                  const text = activePromptType === 'system' ? systemPrompt : userPrompt;
                  handleCopy(text || '');
                }}
                className="btn-copy"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                Copy
              </button>
            </div>
            <div className="output-panel">
              <pre className="panel-content md-highlighted">
                {activePromptType === 'system' 
                  ? highlightMarkdown(systemPrompt)
                  : highlightMarkdown(userPrompt)
                }
              </pre>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

ComparisonResults.propTypes = {
  results: PropTypes.shape({
    model_1: PropTypes.shape({
      model: PropTypes.string,
      output: PropTypes.string,
      latency_ms: PropTypes.number,
      estimated_cost: PropTypes.number,
      token_usage: PropTypes.object,
      status: PropTypes.string
    }),
    model_2: PropTypes.shape({
      model: PropTypes.string,
      output: PropTypes.string,
      latency_ms: PropTypes.number,
      estimated_cost: PropTypes.number,
      token_usage: PropTypes.object,
      status: PropTypes.string
    }),
    llm_config_1: PropTypes.shape({ model: PropTypes.string }),
    llm_config_2: PropTypes.shape({ model: PropTypes.string }),
    output_1: PropTypes.string,
    output_2: PropTypes.string,
    latency_ms_1: PropTypes.number,
    latency_ms_2: PropTypes.number,
    estimated_cost_1: PropTypes.number,
    estimated_cost_2: PropTypes.number,
    token_usage_1: PropTypes.object,
    token_usage_2: PropTypes.object,
    system_prompt: PropTypes.string,
    user_prompt: PropTypes.string,
    project_name: PropTypes.string,
    created_at: PropTypes.string,
    input_values: PropTypes.object,
    status_1: PropTypes.string,
    status_2: PropTypes.string,
    input_cost_1: PropTypes.number,
    output_cost_1: PropTypes.number,
    input_cost_2: PropTypes.number,
    output_cost_2: PropTypes.number
  })
};

export default ComparisonResults;
